import {useRoute} from '@react-navigation/native';
import React, {PropsWithChildren, useEffect, useMemo, useState} from 'react';
import {
  View,
  Text,
  Alert,
  ViewStyle,
  TextStyle,
  ScrollView,
  TouchableOpacity,
  Platform,
} from 'react-native';
import Modal from 'react-native-modal';
import {responsiveWidth} from 'react-native-responsive-dimensions';

import {text} from '../text';
import {svg} from '../assets/svg';
import {theme} from '../constants';
import {setScreen} from '../store/slices/tabSlice';
import BurgerProfileItem from './BurgerProfileItem';
import {statusBarHeight, homeIndicatorHeight} from '../utils';
import {useAppNavigation, useAppSelector, useAppDispatch} from '../hooks';
import {APP_CONFIG, resolveStyleConfig} from '../config';
import {formatCurrency} from '../utils/currency';
import LocationPickerModal from './LocationPickerModal';
import {setLocation} from '../store/slices/locationSlice';
import {formatCoordinate, resolveAddressForCoordinate} from '../utils/location';
import {addLocationPickerListener} from '../utils/locationEvents';
import type {Coordinate} from '../types';

type Props = PropsWithChildren<{
  skip?: boolean;
  title?: string;
  basket?: boolean;
  goBack?: boolean;
  filter?: boolean;
  search?: boolean;
  style?: ViewStyle;
  bottomLine?: boolean;
  burgerIcon?: boolean;
  user?: boolean;
  userName?: boolean;
  userImage?: boolean;
  skipOnPress?: () => void;
  onUserPress?: () => void;
}>;

const Header: React.FC<Props> = ({
  skip,
  title,
  style,
  basket,
  search,
  goBack,
  filter,
  userImage,
  bottomLine,
  skipOnPress,
  onUserPress,
}) => {
  const dispatch = useAppDispatch();
  const navigation = useAppNavigation();

  const [showModal, setShowModal] = useState(false);
  const [notification, setNotification] = useState<boolean>(
    APP_CONFIG.ui.notificationsEnabled ?? false,
  );
  const [faceID, setFaceID] = useState(false);
  const [isLocationPickerVisible, setIsLocationPickerVisible] = useState(false);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);

  useEffect(() => {
    const removeListener = addLocationPickerListener(() => {
      setIsLocationPickerVisible(true);
    });

    return () => {
      removeListener();
    };
  }, []);

  const route = useRoute();

  const commonStyleConfig = useMemo(
    () => resolveStyleConfig('common'),
    [],
  );

  const cart = useAppSelector((state) => state.cartSlice.list);
  const total = useAppSelector((state) => state.cartSlice.total);
  const locationState = useAppSelector((state) => state.locationSlice);

  const defaultCoordinate = useMemo<Coordinate>(
    () => ({
      latitude: APP_CONFIG.location.latitude,
      longitude: APP_CONFIG.location.longitude,
    }),
    [],
  );

  const locationLabel = useMemo(() => {
    if (locationState.isAddressConfirmed && locationState.address) {
      return locationState.address;
    }

    if (locationState.isAddressConfirmed && locationState.coordinate) {
      return formatCoordinate(locationState.coordinate);
    }

    return 'veuillez entrez une adresse';
  }, [locationState]);

  const handleOnPress = () => {
    if (cart.length > 0) {
      dispatch(setScreen('Order'));
      route.name === 'Shop' && navigation.navigate('TabNavigator');
      route.name === 'Product' && navigation.navigate('TabNavigator');
    }
    if (cart.length === 0) {
      Alert.alert('Your cart is empty', 'Please add some items to your cart', [
        {
          text: 'OK',
          onPress: () => console.log('OK Pressed'),
        },
      ]);
    }
  };

  const handleLocationPress = () => {
    if (onUserPress) {
      onUserPress();
      return;
    }

    setIsLocationPickerVisible(true);
  };

  const handleLocationConfirm = async (coordinate: Coordinate) => {
    if (onUserPress) {
      return;
    }

    if (isResolvingAddress) {
      return;
    }

    setIsResolvingAddress(true);
    try {
      const resolvedAddress = await resolveAddressForCoordinate(coordinate);

      dispatch(
        setLocation({
          coordinate,
          address: resolvedAddress,
        }),
      );

      setIsLocationPickerVisible(false);
    } finally {
      setIsResolvingAddress(false);
    }
  };

  const handleBackPress = () => {
    if (canNavigateBack) {
      navigation.goBack();
      return;
    }

    if (Platform.OS === 'web') {
      const globalWindow = typeof window !== 'undefined' ? window : undefined;
      if (globalWindow) {
        globalWindow.history.back();
        return;
      }
    }

    dispatch(setScreen('Home'));
  };

  const canNavigateBack = navigation.canGoBack();
  const shouldShowBackButton = Boolean(goBack);

  const iconColor = theme.colors.textPrimary;
  const iconSize = 24;
  const iconWrapperStyle: ViewStyle = {
    width: iconSize,
    height: iconSize,
    alignItems: 'center',
    justifyContent: 'center',
  };

  const renderUser = (): JSX.Element | null => {
    const touchableStyle: ViewStyle = {
      position: 'absolute',
      flexDirection: 'row',
      alignItems: 'center',
      left: shouldShowBackButton ? 48 : 0,
      paddingVertical: 12,
      paddingHorizontal: 20,
      maxWidth: theme.sizes.width * 0.6,
    };

    const textStyle: TextStyle = {
      marginLeft: 10,
      ...theme.fonts.H5,
      color: commonStyleConfig.colors.text,
      fontFamily: commonStyleConfig.textFont,
      fontSize: commonStyleConfig.size,
      lineHeight: commonStyleConfig.size * 1.2,
    };

    const content = (
      <>
        <View style={iconWrapperStyle}>
          <svg.MapPinSvg size={iconSize} color={iconColor} />
        </View>
        <Text style={{...textStyle}} numberOfLines={1} ellipsizeMode='tail'>
          {locationLabel}
        </Text>
      </>
    );

    if (userImage || onUserPress) {
      return (
        <TouchableOpacity
          style={{...touchableStyle}}
          onPress={handleLocationPress}
          onLongPress={
            userImage
              ? () => {
                  if (!APP_CONFIG.ui.hideProfileMenu) {
                    setShowModal(true);
                  }
                }
              : undefined
          }
          delayLongPress={userImage ? 250 : undefined}
        >
          {content}
        </TouchableOpacity>
      );
    }

    return <View style={{...touchableStyle}}>{content}</View>;
  };

  const renderBurgerLocation = (): JSX.Element | null => {
    if (APP_CONFIG.ui.hideProfileMenu) {
      return null;
    }

    return (
      <View
        style={{
          padding: 20,
          flexDirection: 'row',
          alignItems: 'flex-start',
          borderBottomWidth: 1,
          borderBottomColor: '#DBE9F5',
          marginBottom: 20,
        }}
      >
        <View
          style={{
            width: responsiveWidth(14),
            aspectRatio: 1,
            borderRadius: responsiveWidth(20) / 2,
            backgroundColor: '#E9F3F6',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg.MapPinSvg color={theme.colors.textPrimary} size={24} />
        </View>
        <View style={{marginLeft: 14, flex: 1}}>
          <Text
            style={{
              ...theme.fonts.DMSans_500Medium,
              fontSize: 14,
              lineHeight: 14 * 1.4,
              color: theme.colors.textPrimary,
            }}
          >
            {locationLabel}
          </Text>
        </View>
      </View>
    );
  };

  const renderCloseButton = (): JSX.Element | null => {
    return (
      <TouchableOpacity
        style={{
          position: 'absolute',
          marginTop: statusBarHeight(),
          right: -responsiveWidth(11),
        }}
        onPress={() => setShowModal(false)}
      >
        <svg.CloseSvg />
      </TouchableOpacity>
    );
  };

  const renderBurgerContent = (): JSX.Element | null => {
    return (
      <ScrollView style={{flex: 1, paddingHorizontal: 20}}>
        <BurgerProfileItem
          text={'Personal information'}
          onPress={() => {
            setShowModal(false);
            navigation.navigate('EditProfile');
          }}
        />
        <BurgerProfileItem
          text={'My orders'}
          onPress={() => {
            setShowModal(false);
            navigation.navigate('OrderHistory');
          }}
        />
        <BurgerProfileItem
          text={'Promocodes & gift cards'}
          onPress={() => {
            setShowModal(false);
            navigation.navigate('Promocodes');
          }}
        />
        <BurgerProfileItem
          text={'Wallet'}
          onPress={() => {
            setShowModal(false);
            navigation.navigate('Wallet');
          }}
        />
        {APP_CONFIG.ui.notificationsEnabled ? (
          <BurgerProfileItem
            text={'Notifications'}
            onPress={() => {
              setNotification(!notification);
            }}
            disabled={notification}
          />
        ) : null}
        <BurgerProfileItem
          text={'Face ID'}
          onPress={() => {
            setFaceID(!faceID);
          }}
          disabled={faceID}
        />
        <BurgerProfileItem text={'Support center'} onPress={() => {}} />
        <BurgerProfileItem
          text={'Sign out'}
          onPress={() => {
            setShowModal(false);
            navigation.navigate('SignIn');
          }}
        />
      </ScrollView>
    );
  };

  const renderBurgerProfile = (): JSX.Element | null => {
    if (APP_CONFIG.ui.hideProfileMenu) {
      return null;
    }

    return (
      <Modal
        isVisible={showModal}
        onBackdropPress={() => setShowModal(false)}
        hideModalContentWhileAnimating={true}
        backdropTransitionOutTiming={0}
        style={{margin: 0}}
        animationIn='slideInLeft'
        animationOut='slideOutLeft'
        animationInTiming={500}
        animationOutTiming={500}
        deviceWidth={theme.sizes.height}
        deviceHeight={theme.sizes.height}
      >
        <View
          style={{
            width: responsiveWidth(74),
            height: theme.sizes.height,
            backgroundColor: theme.colors.backgroundPrimary,
            paddingTop: statusBarHeight(),
            paddingBottom: homeIndicatorHeight(),
          }}
        >
          {renderBurgerLocation()}
          {renderBurgerContent()}
          {renderCloseButton()}
        </View>
      </Modal>
    );
  };

  const renderGoBack = (): JSX.Element | null => {
    if (shouldShowBackButton) {
      return (
        <View style={{position: 'absolute', left: 0}}>
          <TouchableOpacity
            style={{
              paddingVertical: 12,
              paddingHorizontal: 20,
            }}
            onPress={handleBackPress}
          >
            <View style={iconWrapperStyle}>
              <svg.GoBackSvg size={iconSize} color={iconColor} />
            </View>
          </TouchableOpacity>
        </View>
      );
    }

    return null;
  };

  const renderSkipText = (): JSX.Element | null => {
    if (skip) {
      return (
        <TouchableOpacity
          style={{
            right: 0,
            position: 'absolute',
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 20,
          }}
          onPress={skipOnPress}
        >
          <Text
            style={{
              fontSize: 14,
              lineHeight: 14 * 1.7,
            }}
          >
            Skip
          </Text>
        </TouchableOpacity>
      );
    }

    return null;
  };

  const renderTitle = (): JSX.Element | null => {
    const titleStyle: TextStyle = {
      ...theme.fonts.DMSans_400Regular,
      fontSize: 16,
      color: theme.colors.textPrimary,
    };

    if (title) {
      return (
        <Text style={{...titleStyle}} numberOfLines={1}>
          {title}
        </Text>
      );
    }

    return null;
  };

  const renderSearch = (): JSX.Element | null => {
    if (search) {
      return (
        <TouchableOpacity
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            width: theme.sizes.width - 190,
            marginRight: 60,
          }}
          onPress={() => navigation.navigate('Search')}
        >
          <View style={{marginRight: 7}}>{/* <svg.SearchSvg /> */}</View>
          <text.T14>search</text.T14>
        </TouchableOpacity>
      );
    }

    return null;
  };

  const renderFilter = (): JSX.Element | null => {
    if (filter) {
      return (
        <View style={{position: 'absolute', right: 0}}>
          <TouchableOpacity
            style={{
              paddingVertical: 12,
              paddingHorizontal: 20,
            }}
            onPress={() => navigation.navigate('Filter')}
          >
            <View style={iconWrapperStyle}>
              {/* <svg.FilterSvg size={iconSize} color={iconColor} /> */}
            </View>
          </TouchableOpacity>
        </View>
      );
    }

    return null;
  };

  const renderBasket = (): JSX.Element | null => {
    if (basket) {
      return (
        <TouchableOpacity
          onPress={handleOnPress}
          style={{
            right: 0,
            position: 'absolute',
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 20,
          }}
        >
          <View
            style={{
              height: 22,
              borderRadius: 11,
              paddingHorizontal: 9,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.colors.accentPrimary,
            }}
          >
            <Text
              style={{
                color: theme.colors.backgroundPrimary,
                ...theme.fonts.DMSans_700Bold,
                fontSize: 12,
              }}
            >
              {formatCurrency(cart.length > 0 ? total : 0)}
            </Text>
          </View>
          <View style={iconWrapperStyle}>
            <svg.CartSvg size={iconSize} color={iconColor} />
          </View>
        </TouchableOpacity>
      );
    }

    return null;
  };

  const renderLocationPicker = (): JSX.Element | null => {
    if (!userImage) {
      return null;
    }

    const coordinateValue = locationState.coordinate ?? defaultCoordinate;

    return (
      <LocationPickerModal
        visible={isLocationPickerVisible}
        value={coordinateValue}
        onClose={() => setIsLocationPickerVisible(false)}
        onConfirm={handleLocationConfirm}
      />
    );
  };

  const containerStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 42,
    paddingVertical: 10,
    backgroundColor: commonStyleConfig.background,
    borderBottomColor: bottomLine ? '#DBE9F5' : 'transparent',
    borderBottomWidth: bottomLine ? 1 : 0,
    ...style,
  };

  return (
    <View style={{...containerStyle}}>
      {renderUser()}
      {renderGoBack()}
      {renderTitle()}
      {renderSkipText()}
      {renderFilter()}
      {renderSearch()}
      {renderBasket()}
      {renderBurgerProfile()}
      {renderLocationPicker()}
    </View>
  );
};

export default Header;
